// Inicio: panel de la empresa seleccionada, en cristal y con accesos
// directos a todas las partes de la app (rediseño oct-2026, estilo "Glassy
// Dashboard" con la paleta PF). Desde nov-2026 es PERSONALIZABLE: rejilla de
// widgets (react-grid-layout) que se mueven, se redimensionan, se configuran
// y se añaden desde una galería (catálogo en components/inicio/widgets.jsx).
// El panel se guarda POR EMPRESA en el backend: lo editan el propietario y
// los invitados con permiso de edición; los de solo lectura lo ven tal cual.
// Solo LEE datos que ya existen: dashboard del último ejercicio, comparativa
// histórica, consolidado de proyección y salud — no calcula nada nuevo.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { useUser } from "@clerk/clerk-react";
import { Responsive } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { toast } from "sonner";
import {
  ArrowUpRight, Building2, Upload, LayoutGrid, Plus, RotateCcw, Check, X, Settings2,
  Copy, GripVertical, Search, Lock,
} from "lucide-react";
import {
  getDashboard, getComparativa, getProyeccionResultado, getSaludEmpresa,
  getDashboardWidgets, saveDashboardWidgets, resetDashboardWidgets,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Marco } from "@/components/inicio/widgetKit";
import { CATALOGO, CATEGORIAS, POR_TIPO, PANEL_DEFECTO, FILAS_PANTALLA } from "@/components/inicio/widgets";

const MARGEN = 12;
const BREAKPOINTS = { lg: 1024, md: 640, sm: 0 };
const COLUMNAS = { lg: 12, md: 6, sm: 1 };

// Alto de fila para que el panel por defecto (10 filas) llene la pantalla sin scroll
function altoFila() {
  const disponible = window.innerHeight - 150 - 72;
  return Math.max(52, Math.floor((disponible - MARGEN * (FILAS_PANTALLA - 1)) / FILAS_PANTALLA));
}

const nuevoId = (tipo) => `${tipo.slice(0, 20)}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// Primer hueco libre para un widget de ancho w: para cada columna posible, la
// fila más baja que ya está libre en ese tramo; gana la más alta (y la de más
// a la izquierda), así los widgets nuevos rellenan huecos antes de ir al final.
function hueco(lista, w) {
  let mejor = { x: 0, y: Infinity };
  for (let x = 0; x <= 12 - w; x += 1) {
    const y = lista
      .filter((o) => o.x < x + w && o.x + o.w > x)
      .reduce((m, o) => Math.max(m, o.y + o.h), 0);
    if (y < mejor.y) mejor = { x, y };
  }
  return mejor;
}

// Solo widgets que existen en el catálogo (un tipo retirado no rompe el panel)
const validos = (lista) => (lista || []).filter((w) => POR_TIPO[w.tipo]);

function BotonBarra({ onClick, icono: Icono, children, primario, testId, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] font-medium transition-colors",
        primario
          ? "bg-[hsl(var(--pill-active))] text-[hsl(var(--pill-active-foreground))] hover:opacity-90"
          : "glass text-foreground hover:bg-foreground/[0.06]"
      )}
    >
      <Icono className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

// Galería: todos los widgets por categoría, con buscador; se pueden añadir
// tantos como se quiera (también repetidos con distinta configuración).
function Galeria({ abierta, onClose, onAnadir, enPanel }) {
  const [cat, setCat] = useState("Todos");
  const [q, setQ] = useState("");
  const lista = CATALOGO.filter((w) => (cat === "Todos" || w.cat === cat)
    && (!q || `${w.nombre} ${w.desc}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <Dialog open={abierta} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden p-0" data-testid="galeria-widgets">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Galería de widgets</DialogTitle>
          <DialogDescription>Añade los que quieras; luego muévelos, cámbiales el tamaño y configúralos.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 px-6">
          <div className="pill-group">
            {["Todos", ...CATEGORIAS].map((c) => (
              <button key={c} onClick={() => setCat(c)} className={cn("pill", cat === c && "pill-active")}>{c}</button>
            ))}
          </div>
          <label className="ml-auto flex h-8 items-center gap-1.5 rounded-full bg-foreground/[0.05] px-3 text-[12px]">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar" className="w-32 bg-transparent outline-none" />
          </label>
        </div>
        <div className="grid max-h-[58vh] grid-cols-1 gap-3 overflow-auto px-6 pb-6 pt-1 sm:grid-cols-2">
          {lista.map((w) => {
            const n = enPanel[w.tipo] || 0;
            return (
              <div key={w.tipo} className="glass flex flex-col gap-2 rounded-2xl p-4" data-testid={`galeria-${w.tipo}`}>
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary"><w.icono className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold">{w.nombre}</div>
                    <div className="text-[11px] leading-snug text-muted-foreground">{w.desc}</div>
                  </div>
                </div>
                <div className="mt-auto flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{w.cat} · {w.w}×{w.h}{n ? ` · ${n} en tu panel` : ""}</span>
                  <button
                    onClick={() => onAnadir(w)}
                    className="inline-flex h-7 items-center gap-1 rounded-full bg-[hsl(var(--pill-active))] px-3 font-medium text-[hsl(var(--pill-active-foreground))] hover:opacity-90"
                    data-testid={`anadir-${w.tipo}`}
                  >
                    <Plus className="h-3 w-3" /> Añadir
                  </button>
                </div>
              </div>
            );
          })}
          {!lista.length && <div className="col-span-full py-8 text-center text-sm text-muted-foreground">Ningún widget coincide.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Configuración de un widget (sus opciones del catálogo)
function Configurar({ widget, onClose, onGuardar }) {
  const def = widget ? POR_TIPO[widget.tipo] : null;
  const [valores, setValores] = useState({});
  useEffect(() => { if (widget) setValores({ ...(def?.defecto || {}), ...(widget.config || {}) }); }, [widget]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!widget || !def) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="configurar-widget">
        <DialogHeader>
          <DialogTitle>Configurar · {def.nombre}</DialogTitle>
          <DialogDescription>{def.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(def.opciones || []).map((o) => (
            <label key={o.clave} className="block text-[12px]">
              <span className="mb-1 block font-medium">{o.etiqueta}</span>
              {o.tipo === "select" ? (
                <select
                  value={valores[o.clave] ?? ""}
                  onChange={(e) => setValores((v) => ({ ...v, [o.clave]: e.target.value }))}
                  className="h-9 w-full rounded-xl border border-border bg-background/60 px-3 text-sm"
                  data-testid={`opcion-${o.clave}`}
                >
                  {o.opciones.map((op) => <option key={op.v} value={op.v}>{op.l}</option>)}
                </select>
              ) : o.largo ? (
                <textarea
                  value={valores[o.clave] ?? ""}
                  maxLength={o.max}
                  rows={3}
                  onChange={(e) => setValores((v) => ({ ...v, [o.clave]: e.target.value }))}
                  className="w-full rounded-xl border border-border bg-background/60 px-3 py-2 text-sm"
                  data-testid={`opcion-${o.clave}`}
                />
              ) : (
                <input
                  value={valores[o.clave] ?? ""}
                  maxLength={o.max}
                  onChange={(e) => setValores((v) => ({ ...v, [o.clave]: e.target.value }))}
                  className="h-9 w-full rounded-xl border border-border bg-background/60 px-3 text-sm"
                  data-testid={`opcion-${o.clave}`}
                />
              )}
              {o.max && <span className="mt-0.5 block text-right text-[10px] text-muted-foreground">{String(valores[o.clave] ?? "").length}/{o.max}</span>}
            </label>
          ))}
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <BotonBarra onClick={onClose} icono={X}>Cancelar</BotonBarra>
          <BotonBarra onClick={() => onGuardar(valores)} icono={Check} primario testId="configurar-aplicar">Aplicar</BotonBarra>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function HomePage() {
  const { empresa, ejercicios = [] } = useOutletContext() || {};
  const { user } = useUser();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [comp, setComp] = useState([]);
  const [proy, setProy] = useState(null);
  const [salud, setSalud] = useState(null);
  const [cargando, setCargando] = useState(false);
  // Panel: guardado (o por defecto), permiso y borrador en modo edición
  const [guardados, setGuardados] = useState(null);
  const [esPersonalizado, setEsPersonalizado] = useState(false);
  const [puedeEditar, setPuedeEditar] = useState(false);
  const [borrador, setBorrador] = useState(null);
  const [galeria, setGaleria] = useState(false);
  const [configurando, setConfigurando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [bp, setBp] = useState("lg");
  const [fila, setFila] = useState(altoFila);
  // Ancho de la rejilla medido aquí (el WidthProvider de la librería se queda
  // en 0 si el primer render ocurre con la ventana oculta)
  const contRef = useRef(null);
  const [ancho, setAncho] = useState(0);

  const ultimo = ejercicios[0] || null; // ejercicios llegan ordenados de más reciente a más antiguo
  const editando = borrador !== null;

  useEffect(() => {
    const r = () => setFila(altoFila());
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  useEffect(() => {
    const el = contRef.current;
    if (!el) return undefined;
    const medir = () => setAncho(el.clientWidth);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [empresa?.id, ultimo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let vivo = true;
    setDash(null); setComp([]); setProy(null); setSalud(null);
    setGuardados(null); setBorrador(null); setEsPersonalizado(false); setPuedeEditar(false);
    if (!empresa) return undefined;
    getDashboardWidgets(empresa.id)
      .then(({ data }) => {
        if (!vivo) return;
        setPuedeEditar(!!data.puede_editar);
        setEsPersonalizado(Array.isArray(data.widgets));
        setGuardados(Array.isArray(data.widgets) ? validos(data.widgets) : PANEL_DEFECTO);
      })
      .catch(() => vivo && setGuardados(PANEL_DEFECTO));
    if (!ultimo) return () => { vivo = false; };
    setCargando(true);
    const anios = ejercicios.map((e) => e.anio).sort((a, b) => a - b).join(",");
    Promise.allSettled([
      getDashboard(ultimo.id),
      getComparativa(empresa.id, anios),
      getProyeccionResultado(empresa.id),
      getSaludEmpresa(empresa.id),
    ]).then(([d, c, p, s]) => {
      if (!vivo) return;
      if (d.status === "fulfilled") setDash(d.value.data);
      if (c.status === "fulfilled") setComp([...(c.value.data || [])].sort((a, b) => a.anio - b.anio));
      if (p.status === "fulfilled") setProy(p.value.data);
      if (s.status === "fulfilled") setSalud(s.value.data);
      setCargando(false);
    });
    return () => { vivo = false; };
  }, [empresa?.id, ultimo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const widgets = useMemo(() => borrador || guardados || [], [borrador, guardados]);
  const layout = useMemo(() => widgets.map((w) => {
    const def = POR_TIPO[w.tipo];
    return { i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, minW: Math.min(def.minW, 12), minH: def.minH, maxH: 12 };
  }), [widgets]);
  const enPanel = useMemo(() => widgets.reduce((acc, w) => ({ ...acc, [w.tipo]: (acc[w.tipo] || 0) + 1 }), {}), [widgets]);

  const nombre = user?.firstName || (user?.fullName || "").split(" ")[0] || "";

  // ── Sin empresa / sin ejercicios: invitación clara, no una pantalla vacía ──
  if (!empresa || !ultimo) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex h-[calc(100vh-150px)] min-h-[420px] items-center justify-center">
        <div className="max-w-lg">
          <Marco className="p-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
              {empresa ? <Upload className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
            </div>
            <h1 className="text-2xl font-light tracking-tight">{nombre ? `Hola, ${nombre}` : "Bienvenido a Finreport"}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {empresa
                ? `${empresa.nombre} todavía no tiene ejercicios importados. Importa su Sumas y Saldos para ver el panel.`
                : "Crea tu primera empresa para empezar a analizar su Sumas y Saldos."}
            </p>
            <button
              onClick={() => navigate(empresa ? `/empresas/${empresa.id}` : "/empresas")}
              className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--pill-active))] px-5 py-2 text-sm font-medium text-[hsl(var(--pill-active-foreground))]"
            >
              {empresa ? "Importar ejercicio" : "Crear empresa"} <ArrowUpRight className="h-4 w-4" />
            </button>
          </Marco>
        </div>
      </motion.div>
    );
  }

  const datos = { empresa, ultimo, ejercicios, base: `/empresas/${empresa.id}`, dash, comp, proy, salud };
  const parcial = dash?.periodo?.parcial;

  // ── Acciones del modo edición ──
  const empezar = () => setBorrador((guardados || PANEL_DEFECTO).map((w) => ({ ...w, config: { ...(w.config || {}) } })));
  const cancelar = () => { setBorrador(null); setGaleria(false); };
  const alMover = (nuevo) => {
    // Solo la rejilla ancha (12 columnas) es la que se guarda
    if (!editando || bp !== "lg") return;
    const pos = Object.fromEntries(nuevo.map((l) => [l.i, l]));
    setBorrador((b) => b.map((w) => (pos[w.id] ? { ...w, x: pos[w.id].x, y: pos[w.id].y, w: pos[w.id].w, h: pos[w.id].h } : w)));
  };
  const anadir = (def) => {
    setBorrador((b) => [...b, { id: nuevoId(def.tipo), tipo: def.tipo, ...hueco(b, def.w), w: def.w, h: def.h, config: { ...(def.defecto || {}) } }]);
    toast.success(`${def.nombre} añadido al panel`);
  };
  const quitar = (id) => setBorrador((b) => b.filter((w) => w.id !== id));
  const duplicar = (w) => setBorrador((b) => [...b, { ...w, id: nuevoId(w.tipo), ...hueco(b, w.w), config: { ...w.config } }]);
  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await saveDashboardWidgets(empresa.id, borrador);
      setGuardados(validos(data.widgets));
      setEsPersonalizado(true);
      setBorrador(null);
      toast.success("Panel guardado para esta empresa");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "No tienes permiso de edición en esta empresa" : "No se pudo guardar el panel");
    } finally {
      setGuardando(false);
    }
  };
  const restablecer = async () => {
    if (!window.confirm("¿Volver al panel por defecto? Se perderá la personalización de esta empresa.")) return;
    try {
      await resetDashboardWidgets(empresa.id);
      setGuardados(PANEL_DEFECTO);
      setEsPersonalizado(false);
      setBorrador(null);
      toast.success("Panel restablecido");
    } catch {
      toast.error("No se pudo restablecer el panel");
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-150px)] flex-col gap-3" data-testid="home-dashboard">
      {/* Saludo + barra de personalización */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <h1 className="text-[2rem] font-light leading-tight tracking-tight">
            {nombre ? <>Hola, <span className="font-medium">{nombre}</span></> : "Bienvenido"}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {empresa.nombre} · Ejercicio {ultimo.anio}
            {parcial && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-300">parcial · {dash.periodo.dias} días</span>}
            {cargando && <span className="ml-2 text-[11px]">cargando…</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {editando ? (
            <>
              <span className="mr-1 text-[11px] text-muted-foreground">
                {bp === "lg" ? "Arrastra para mover · esquina para redimensionar" : "Amplía la ventana para colocar los widgets"}
              </span>
              <BotonBarra onClick={() => setGaleria(true)} icono={Plus} testId="abrir-galeria">Añadir widget</BotonBarra>
              {esPersonalizado && <BotonBarra onClick={restablecer} icono={RotateCcw} testId="restablecer-panel" title="Volver al panel por defecto">Restablecer</BotonBarra>}
              <BotonBarra onClick={cancelar} icono={X} testId="cancelar-panel">Cancelar</BotonBarra>
              <BotonBarra onClick={guardar} icono={Check} primario testId="guardar-panel">{guardando ? "Guardando…" : "Guardar"}</BotonBarra>
            </>
          ) : puedeEditar ? (
            <BotonBarra onClick={empezar} icono={LayoutGrid} testId="personalizar-panel">Personalizar</BotonBarra>
          ) : guardados && (
            <span className="inline-flex items-center gap-1 rounded-full bg-foreground/[0.05] px-3 py-1.5 text-[11px] text-muted-foreground" title="Solo el propietario y quien tenga permiso de edición pueden cambiar este panel">
              <Lock className="h-3 w-3" /> Panel de solo lectura
            </span>
          )}
        </div>
      </motion.div>

      {editando && !widgets.length && (
        <div className="glass flex flex-col items-center gap-3 rounded-[1.5rem] p-10 text-center text-sm text-muted-foreground">
          El panel está vacío.
          <BotonBarra onClick={() => setGaleria(true)} icono={Plus} primario>Añadir widget</BotonBarra>
        </div>
      )}

      <div ref={contRef} className="-mx-[12px]">
      {guardados && ancho > 0 && (
        <Responsive
          width={ancho}
          className={cn(editando && "panel-editando")}
          layouts={{ lg: layout }}
          breakpoints={BREAKPOINTS}
          cols={COLUMNAS}
          rowHeight={fila}
          margin={[MARGEN, MARGEN]}
          isDraggable={editando && bp === "lg"}
          isResizable={editando && bp === "lg"}
          draggableHandle=".widget-asa"
          draggableCancel=".widget-no-arrastrar"
          resizeHandles={["se"]}
          onBreakpointChange={setBp}
          onLayoutChange={alMover}
        >
          {widgets.map((w, i) => {
            const def = POR_TIPO[w.tipo];
            const C = def.C;
            return (
              <div key={w.id} data-testid={`widget-${w.tipo}`}>
                <motion.div
                  className="relative h-full"
                  initial={{ opacity: 0, y: 16, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 26, delay: Math.min(i, 12) * 0.04 }}
                  whileHover={editando ? undefined : { y: -3 }}
                >
                  <C d={datos} config={{ ...(def.defecto || {}), ...(w.config || {}) }} />
                  {editando && (
                    <div className="widget-asa absolute inset-0 cursor-move rounded-[1.5rem] ring-2 ring-primary/50 ring-offset-0 bg-background/10 backdrop-blur-[1px]">
                      <div className="widget-no-arrastrar absolute right-2 top-2 flex items-center gap-1">
                        {def.opciones?.length > 0 && (
                          <button onClick={() => setConfigurando(w)} title="Configurar" className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--pill-active))] text-[hsl(var(--pill-active-foreground))]" data-testid={`configurar-${w.id}`}>
                            <Settings2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={() => duplicar(w)} title="Duplicar" className="flex h-7 w-7 items-center justify-center rounded-full glass-strong text-foreground">
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => quitar(w.id)} title="Quitar del panel" className="flex h-7 w-7 items-center justify-center rounded-full bg-rose-500 text-white" data-testid={`quitar-${w.id}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full glass-strong px-2 py-1 text-[10px] font-medium text-foreground">
                        <GripVertical className="h-3 w-3" /> {def.nombre}
                      </span>
                    </div>
                  )}
                </motion.div>
              </div>
            );
          })}
        </Responsive>
      )}
      </div>

      {editando && (
        <Galeria abierta={galeria} onClose={() => setGaleria(false)} onAnadir={anadir} enPanel={enPanel} />
      )}
      <Configurar
        widget={configurando}
        onClose={() => setConfigurando(null)}
        onGuardar={(config) => {
          setBorrador((b) => b.map((w) => (w.id === configurando.id ? { ...w, config } : w)));
          setConfigurando(null);
        }}
      />
    </div>
  );
}
